import { ApiError } from './api.js';

/**
 * The signed-in session, as far as the browser holds it.
 *
 * The tokens live in `localStorage` for now, keyed simply. This is the pragmatic launch position,
 * not the final one: an access token in `localStorage` is readable by any script on the page, so the
 * strict CSP the portal ships under is what keeps a stranger's script off it. The stronger shape —
 * the refresh token in an HttpOnly cookie, the access token in memory — is a hardening step, and it
 * is where `authFlows.userSrp` and the cookie plumbing are headed.
 *
 * Nothing here authorises anything. The token is presented to the API, and the API decides. A token
 * that the browser thinks is valid but the server rejects is simply a signed-out session.
 */
const KEY = 'ts.session';

export interface Session {
  readonly idToken: string;
  readonly accessToken: string;
  readonly email: string;
}

const BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';

export function currentSession(): Session | null {
  const raw = localStorage.getItem(KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  localStorage.removeItem(KEY);
}

/**
 * Sign in, and remember it.
 *
 * The email is stored beside the tokens only to greet the person by it; it is never trusted as
 * identity — the id token carries that, and the server reads it from there.
 */
export async function signIn(email: string, password: string): Promise<Session> {
  const response = await fetch(`${BASE}/v1/account/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const text = await response.text();
  const body: unknown = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const error = body as { error?: string; message?: string };
    throw new ApiError(response.status, error.error ?? 'INTERNAL', error.message ?? 'Sign-in failed.');
  }

  const tokens = body as { idToken: string; accessToken: string };
  const session: Session = { idToken: tokens.idToken, accessToken: tokens.accessToken, email };
  localStorage.setItem(KEY, JSON.stringify(session));
  return session;
}

/**
 * Set up the account after payment, and remember the session it returns.
 *
 * The email is not passed — the server takes it from the paid purchase, so the account can only be
 * for the person who paid, under the address they paid with. On success the tokens come back and the
 * person is signed in without a second step.
 */
export async function setupAccount(
  purchaseId: string,
  fullName: string,
  password: string,
): Promise<Session> {
  const response = await fetch(`${BASE}/v1/checkout/setup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ purchaseId, fullName, password }),
  });

  const text = await response.text();
  const body: unknown = text ? JSON.parse(text) : {};

  if (!response.ok && response.status !== 201) {
    const error = body as { error?: string; message?: string };
    throw new ApiError(
      response.status,
      error.error ?? 'INTERNAL',
      error.message ?? 'We could not set up your account.',
    );
  }

  const tokens = body as { idToken: string; accessToken: string; email: string };
  const session: Session = {
    idToken: tokens.idToken,
    accessToken: tokens.accessToken,
    email: tokens.email,
  };
  localStorage.setItem(KEY, JSON.stringify(session));
  return session;
}
